import {
  Article,
  ArticleAction,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import { Badge } from "@babackapp/ui/components/badge";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import {
  useLocalizeLineItem,
  useLocalizeProductOptions,
} from "@babackapp/return-widget-components/hooks/use-localize-line-item";
import { DiscountedPrice } from "@babackapp/return-widget-components/components/discounted-price";
import type {
  DraftLineItem,
  Order,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { Trash2Icon } from "lucide-react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { getReferenceTransitionParts } from "@babackapp/return-variables-registry";
import { ExchangeArticleImage, getExchangeArticle } from "./exchange-article";
import { ReferenceFooter } from "./reference-footer";

type Props = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  order: Order | null;
  currency: string;
  isRetractation?: boolean;
  /** SKU footer on screen (`useProductReferencesVisibility().shouldShow`). */
  showReferences?: boolean;
  /**
   * "Only you see this" tooltip next to the SKUs, passed only when the
   * logged-in merchant alone sees them (`isMerchantOnly`).
   */
  referencesHint?: string;
  onRemove: () => void;
};

export function SummaryLineItem({
  item,
  orderLineItems,
  order,
  currency,
  isRetractation,
  showReferences = false,
  referencesHint,
  onRemove,
}: Props) {
  const t = useCustomText();
  const localize = useLocalizeLineItem();
  const translateOptions = useLocalizeProductOptions();
  const display = getLineItemDisplay(
    orderLineItems.map(localize),
    item.lineItemId,
  );
  const { title, image, price, paidPrice, sku } = display;

  const { variantDisplay, exchangeTitle, exchangeImage, exchangeSku } =
    getExchangeArticle(order, item, display, translateOptions);
  // "A → B", "— → B" when the purchased article has no SKU, "A" alone.
  const references = getReferenceTransitionParts(sku, exchangeSku);

  return (
    <Article>
      <ExchangeArticleImage
        src={image}
        exchangeSrc={exchangeImage}
        exchangeAlt={exchangeTitle ?? title}
        layoutId={`img-${item.lineItemId}`}
      />
      <ArticleTitle className="flex flex-wrap items-center gap-x-1.5">
        {title}
        {exchangeTitle && (
          <span className="font-normal text-muted-foreground">
            → {exchangeTitle}
          </span>
        )}
        {isRetractation && (
          <Badge variant="secondary" size="xs">
            {t("returnMethod.retractation.label")}
          </Badge>
        )}
      </ArticleTitle>
      <ArticleSubtitle>
        {[variantDisplay, `×${item.quantity}`].filter(Boolean).join(" — ")}
        {price != null && (
          <>
            {" — "}
            <DiscountedPrice
              catalogCents={price}
              paidCents={paidPrice}
              quantity={item.quantity}
              currency={currency}
            />
          </>
        )}
      </ArticleSubtitle>
      {showReferences && references && (
        <ReferenceFooter {...references} hint={referencesHint} />
      )}
      <ArticleAction autoHide={false}>
        <Button variant="outline" size="icon-sm" onClick={onRemove}>
          <Trash2Icon />
        </Button>
      </ArticleAction>
    </Article>
  );
}
